/**
 * Global Hotkeys — React equivalent of AngularJS Mousetrap bindings.
 *
 * Source:
 *   - public/index.js lines 594-611 (Ctrl+Alt+A/T, Ctrl+Alt+Z/H)
 *   - public/SPIGlassApp/texts/propertyTextPanel.js lines 277-280 (Ctrl+Shift+Arrow)
 *
 * Bindings:
 *   Ctrl+Alt+A / Ctrl+Alt+T  → toggle property text modal (requires G_PROPERTY)
 *   Ctrl+Alt+Z / Ctrl+Alt+H  → toggle work orders modal (requires G_PROPERTY)
 *   Ctrl+Shift+Right          → NEXT_ORDER
 *   Ctrl+Shift+Left           → PREVIOUS_ORDER
 *   Ctrl+Shift+Down           → NEXT_TEXT
 *   Ctrl+Shift+Up             → PREVIOUS_TEXT
 */

import { useEffect } from 'react';
import { useModalStore } from '@/store/modalStore';
import { broadcastAngularEvent } from '@/lib/broadcastAngularEvent';
import { usePropertyStore } from '@/store/propertyStore';
import { getMountConfig } from '@/mountConfig';

function isTypingInEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useGlobalHotkeys(): void {
  useEffect(() => {
    // In embedded mode, AngularJS owns the Mousetrap bindings — skip to avoid double-toggle
    if (getMountConfig().mode === 'embedded') return;

    const handler = (e: KeyboardEvent) => {
      // Don't fire when user is typing — matches Mousetrap default
      if (isTypingInEditable(e.target)) return;

      const key = e.key.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      const alt = e.altKey;
      const shift = e.shiftKey;

      // Ctrl+Alt+A or Ctrl+Alt+T → property text modal
      if (ctrl && alt && !shift && (key === 'a' || key === 't')) {
        const property = usePropertyStore.getState().property;
        if (property) {
          e.preventDefault();
          useModalStore.getState().toggle('propertyText');
        }
        return;
      }

      // Ctrl+Alt+Z or Ctrl+Alt+H → work orders modal
      if (ctrl && alt && !shift && (key === 'z' || key === 'h')) {
        const property = usePropertyStore.getState().property;
        if (property) {
          e.preventDefault();
          useModalStore.getState().toggle('workOrders');
        }
        return;
      }

      // Ctrl+Shift+Arrow → navigate orders/texts (embedded mode only)
      if (ctrl && shift && !alt) {
        if (key === 'arrowright') {
          e.preventDefault();
          broadcastAngularEvent('NEXT_ORDER');
        } else if (key === 'arrowleft') {
          e.preventDefault();
          broadcastAngularEvent('PREVIOUS_ORDER');
        } else if (key === 'arrowdown') {
          e.preventDefault();
          broadcastAngularEvent('NEXT_TEXT');
        } else if (key === 'arrowup') {
          e.preventDefault();
          broadcastAngularEvent('PREVIOUS_TEXT');
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
}
